// CallWaveform.js
// Real, voice-reactive waveform driven by the live call's audio analyser
// (RetellWebClient.analyzerComponent.analyser). Bars scale with the actual
// audio spectrum in real time. Bar HEIGHTS are set directly on the DOM inside
// a requestAnimationFrame loop — NO React re-render per frame, so it's cheap.
//
// Look: a symmetric bell envelope (tall in the middle, short at the edges).
// By default the bars use the purple→blue gradient from the CSS module; pass
// `color` for a solid fill (e.g. white bars in the call banner). When no
// analyser is available yet (call connecting) the bars idle in a gentle
// breathing bell.
import React, { useEffect, useMemo, useRef } from "react";
import { useGlobalCall } from "../../contexts/GlobalCallContext";
import styles from "./CallWaveform.module.css";

const CallWaveform = ({
  bars = 13,
  color,            // solid override; omit to use the CSS purple→blue gradient
  minHeight = 4,
  maxHeight = 40,
  barWidth = 4,
  gap = 4,
}) => {
  const { getCallAnalyser, getMicAnalyser } = useGlobalCall();
  const barRefs = useRef([]);

  // Per-bar envelope (0..1): 1 at the centre, tapering to the edges.
  const envelope = useMemo(() => {
    const half = (bars - 1) / 2 || 1;
    return Array.from({ length: bars }, (_, i) => {
      const dist = Math.abs(i - (bars - 1) / 2) / half; // 0 centre → 1 edge
      return Math.pow(1 - dist, 1.7) * 0.9 + 0.1;       // centre 1 → edge ~0.1
    });
  }, [bars]);

  useEffect(() => {
    let raf;
    let agentBuf = null;
    let micBuf = null;
    let t = 0; // idle animation clock
    const cur = new Array(bars).fill(minHeight); // eased (displayed) heights
    const SMOOTH = 0.12; // 0..1 — lower = smoother glide

    const read = (analyser, buf) => {
      if (!analyser) return null;
      if (!buf || buf.length !== analyser.frequencyBinCount) {
        buf = new Uint8Array(analyser.frequencyBinCount);
      }
      try {
        analyser.getByteFrequencyData(buf);
      } catch (_e) {
        return null;
      }
      return buf;
    };

    const tick = () => {
      t += 0.05;
      const agent = typeof getCallAnalyser === "function" ? getCallAnalyser() : null;
      const mic = typeof getMicAnalyser === "function" ? getMicAnalyser() : null;
      const aData = read(agent, agentBuf);
      const mData = read(mic, micBuf);
      if (aData) agentBuf = aData;
      if (mData) micBuf = mData;
      const live = !!(aData || mData);

      const n = barRefs.current.length;
      for (let i = 0; i < n; i++) {
        const dist = Math.abs(i - (n - 1) / 2);
        const env = envelope[i] ?? 1;
        let target;
        if (live) {
          let v = 0;
          if (aData) {
            const idx = Math.min(aData.length - 1, Math.floor((dist / (n / 2)) * aData.length * 0.45));
            v = Math.max(v, (aData[idx] || 0) / 255);
          }
          if (mData) {
            const idx = Math.min(mData.length - 1, Math.floor((dist / (n / 2)) * mData.length * 0.45));
            v = Math.max(v, (mData[idx] || 0) / 255);
          }
          const ceiling = maxHeight * env;
          target = minHeight + v * (ceiling - minHeight);
        } else {
          const breathe = 0.12 + 0.06 * (0.5 + 0.5 * Math.sin(t + i * 0.35));
          target = minHeight + env * (maxHeight - minHeight) * breathe;
        }
        cur[i] += (target - cur[i]) * SMOOTH; // ease toward target
        const el = barRefs.current[i];
        if (el) el.style.height = `${cur[i]}px`;
      }
      raf = requestAnimationFrame(tick);
    };

    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [getCallAnalyser, getMicAnalyser, envelope, minHeight, maxHeight, bars]);

  return (
    <div className={styles.wave} style={{ height: maxHeight, gap }} aria-hidden="true">
      {Array.from({ length: bars }).map((_, i) => (
        <span
          key={i}
          ref={(el) => (barRefs.current[i] = el)}
          className={styles.bar}
          style={{
            width: barWidth,
            height: minHeight + envelope[i] * (maxHeight - minHeight) * 0.15,
            ...(color ? { background: color } : {}),
          }}
        />
      ))}
    </div>
  );
};

export default CallWaveform;
